import { useState, useEffect, useMemo } from 'react';
import api from '../../services/api';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import {
  Gamepad2, Server, Search, CheckCircle2, XCircle,
  ToggleLeft, ToggleRight, RefreshCw, Layers,
} from 'lucide-react';
import { Input } from '../../components/ui/input';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Tenant Catalog: per-tenant game & provider on/off toggles.
 *
 * - Tenant admin → acts on own tenant (scope hidden).
 * - Master admin → can pick a tenant from the dropdown (or leave on "main").
 *
 * New games/providers added by master admin auto-appear here with
 * tenant_enabled=true (default-on) — tenants can disable what they don't want.
 */
export default function TenantCatalogPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState('games'); // 'games' | 'providers'
  const [games, setGames] = useState([]);
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null); // id currently toggling
  const [search, setSearch] = useState('');
  const [tenants, setTenants] = useState([]);
  const [selectedTenantId, setSelectedTenantId] = useState('main');
  const [providerFilter, setProviderFilter] = useState('all');

  const isMaster = !!user?.is_master_admin;
  const scopeLabel = useMemo(() => {
    if (!user) return 'Loading…';
    if (!isMaster) return 'Your tenant';
    if (selectedTenantId === 'main') return 'Main (non-tenant) scope';
    const t = tenants.find(t => t.id === selectedTenantId);
    return t ? `${t.name} (${t.slug})` : 'Selected tenant';
  }, [user, isMaster, selectedTenantId, tenants]);

  // Master-only: fetch tenants list once
  useEffect(() => {
    if (!isMaster) return;
    (async () => {
      try {
        const tRes = await api.get('/api/admin/tenants');
        setTenants(tRes.data.tenants || tRes.data.items || []);
      } catch { /* non-fatal */ }
    })();
  }, [isMaster]);

  useEffect(() => { if (user) fetchData(); /* eslint-disable-next-line */ }, [tab, selectedTenantId, user?.id]);

  const qs = () => (isMaster ? `?tenant_id=${encodeURIComponent(selectedTenantId)}` : '');

  const fetchData = async () => {
    setLoading(true);
    try {
      if (tab === 'games') {
        const res = await api.get(`/api/admin/tenant-catalog/games${qs()}`);
        setGames(res.data.items || []);
      } else {
        const res = await api.get(`/api/admin/tenant-catalog/providers${qs()}`);
        setProviders(res.data.items || []);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load catalog');
    } finally { setLoading(false); }
  };

  const toggleGame = async (game) => {
    setSaving(game.id);
    const nextEnabled = !game.tenant_enabled;
    // Optimistic update
    setGames(prev => prev.map(g => g.id === game.id ? { ...g, tenant_enabled: nextEnabled } : g));
    try {
      await api.patch(`/api/admin/tenant-catalog/games/${game.id}${qs()}`, { enabled: nextEnabled });
      toast.success(`${game.name} ${nextEnabled ? 'enabled' : 'disabled'}`);
    } catch (e) {
      // Revert on failure
      setGames(prev => prev.map(g => g.id === game.id ? { ...g, tenant_enabled: !nextEnabled } : g));
      toast.error(e?.response?.data?.detail || 'Update failed');
    } finally { setSaving(null); }
  };

  const toggleProvider = async (p) => {
    setSaving(p.provider_code);
    const nextEnabled = !p.tenant_enabled;
    setProviders(prev => prev.map(x => x.provider_code === p.provider_code ? { ...x, tenant_enabled: nextEnabled } : x));
    try {
      await api.patch(`/api/admin/tenant-catalog/providers/${p.provider_code}${qs()}`, { enabled: nextEnabled });
      toast.success(`${p.name} ${nextEnabled ? 'enabled' : 'disabled'} — affects ${p.game_count} games`);
    } catch (e) {
      setProviders(prev => prev.map(x => x.provider_code === p.provider_code ? { ...x, tenant_enabled: !nextEnabled } : x));
      toast.error(e?.response?.data?.detail || 'Update failed');
    } finally { setSaving(null); }
  };

  const bulkSet = async (value) => {
    if (!games.length) return;
    const visible = filteredGames.map(g => g.id);
    if (!visible.length) return;
    if (!window.confirm(`${value ? 'Enable' : 'Disable'} ${visible.length} games for ${scopeLabel}?`)) return;
    setLoading(true);
    try {
      await api.post(`/api/admin/tenant-catalog/games/bulk${qs()}`, { ids: visible, enabled: value });
      toast.success(`${visible.length} games ${value ? 'enabled' : 'disabled'}`);
      fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Bulk update failed');
      setLoading(false);
    }
  };

  const providerOptions = useMemo(() => {
    const s = new Set();
    games.forEach(g => g.provider_code && s.add(g.provider_code));
    return Array.from(s).sort();
  }, [games]);

  const filteredGames = useMemo(() => {
    const q = search.trim().toLowerCase();
    return games.filter(g => {
      if (providerFilter !== 'all' && (g.provider_code || '') !== providerFilter) return false;
      if (!q) return true;
      return (g.name || '').toLowerCase().includes(q)
        || (g.provider_code || '').toLowerCase().includes(q);
    });
  }, [games, search, providerFilter]);

  const enabledCount = games.filter(g => g.tenant_enabled).length;
  const providerEnabled = providers.filter(p => p.tenant_enabled).length;

  return (
    <div className="space-y-5" data-testid="tenant-catalog-page">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <Layers className="w-6 h-6 text-[#D4AF37]" /> Games & Providers Catalog
          </h2>
          <p className="text-xs text-gray-400 mt-0.5">
            Pick exactly which games and providers your players see.
            <span className="ml-2 text-[#D4AF37]">Scope: {scopeLabel}</span>
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isMaster && (
            <select
              value={selectedTenantId}
              onChange={(e) => setSelectedTenantId(e.target.value)}
              className="h-9 px-3 rounded-lg bg-[#0B0E14] border border-white/10 text-sm text-gray-200"
              data-testid="tenant-selector"
            >
              <option value="main">Main (non-tenant)</option>
              {tenants.map(t => (
                <option key={t.id} value={t.id}>{t.name} · {t.slug}</option>
              ))}
            </select>
          )}
          <button
            onClick={fetchData}
            className="p-2 rounded-lg bg-[#0B0E14] border border-white/10 text-gray-400 hover:text-white transition"
            data-testid="refresh-catalog"
            title="Refresh"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-white/5">
        <TabButton active={tab === 'games'} onClick={() => setTab('games')} icon={Gamepad2} label={games.length ? `Games (${enabledCount}/${games.length})` : 'Games'} testid="tab-games" />
        <TabButton active={tab === 'providers'} onClick={() => setTab('providers')} icon={Server} label={providers.length ? `Providers (${providerEnabled}/${providers.length})` : 'Providers'} testid="tab-providers" />
      </div>

      {tab === 'games' && (
        <>
          {/* Controls */}
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search games by name or provider…"
                className="pl-9 h-10 bg-[#0B0E14] border-white/10 text-white"
                data-testid="games-search"
              />
            </div>
            {providerOptions.length > 0 && (
              <select
                value={providerFilter}
                onChange={(e) => setProviderFilter(e.target.value)}
                className="h-10 px-3 rounded-lg bg-[#0B0E14] border border-white/10 text-sm text-gray-200"
                data-testid="games-provider-filter"
              >
                <option value="all">All providers ({providerOptions.length})</option>
                {providerOptions.map(p => <option key={p} value={p}>{formatProvider(p)}</option>)}
              </select>
            )}
            <button onClick={() => bulkSet(true)} className="px-3 h-10 rounded-lg bg-green-500/10 text-green-400 text-xs font-semibold border border-green-500/20 hover:bg-green-500/20" data-testid="bulk-enable">Enable all visible</button>
            <button onClick={() => bulkSet(false)} className="px-3 h-10 rounded-lg bg-red-500/10 text-red-400 text-xs font-semibold border border-red-500/20 hover:bg-red-500/20" data-testid="bulk-disable">Disable all visible</button>
          </div>

          {/* Grid */}
          {loading && !games.length ? (
            <div className="flex items-center justify-center h-40"><div className="spinner" /></div>
          ) : filteredGames.length === 0 ? (
            <EmptyState message={games.length === 0 ? 'No games in the catalog yet' : 'No games match your filters'} />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
              {filteredGames.map(g => (
                <GameCard key={g.id} game={g} saving={saving === g.id} onToggle={() => toggleGame(g)} />
              ))}
            </div>
          )}
        </>
      )}

      {tab === 'providers' && (
        <>
          {loading && !providers.length ? (
            <div className="flex items-center justify-center h-40"><div className="spinner" /></div>
          ) : providers.length === 0 ? (
            <EmptyState message="No providers in the catalog yet" />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {providers.map(p => (
                <ProviderCard key={p.provider_code} provider={p} saving={saving === p.provider_code} onToggle={() => toggleProvider(p)} />
              ))}
            </div>
          )}
          <div className="rounded-xl border border-amber-500/20 bg-amber-500/5 px-4 py-3 text-xs text-amber-300">
            Disabling a provider hides <span className="font-semibold">all its games</span> from your players, even if the individual game toggles are on.
          </div>
        </>
      )}
    </div>
  );
}

function TabButton({ active, onClick, icon: Icon, label, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold transition border-b-2 -mb-px ${
        active ? 'text-white border-[#D4AF37]' : 'text-gray-400 border-transparent hover:text-white'
      }`}
    >
      <Icon className="w-4 h-4" /> {label}
    </button>
  );
}

function GameCard({ game, saving, onToggle }) {
  const logo = resolveUrl(game.logo_url);
  return (
    <div
      className={`rounded-xl p-3 flex items-center gap-3 border transition ${
        game.tenant_enabled
          ? 'border-white/10 bg-[#131822]'
          : 'border-white/5 bg-[#131822]/60 opacity-75'
      }`}
      data-testid={`game-card-${game.id}`}
    >
      <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0 flex items-center justify-center" style={{ background: 'rgba(108,92,231,0.15)' }}>
        {logo ? (
          <img src={logo} alt={game.name} className="w-full h-full object-cover" />
        ) : (
          <Gamepad2 className="w-6 h-6 text-purple-400" />
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 min-w-0">
          <p className="text-sm font-semibold text-white truncate" title={game.name}>{game.name}</p>
          {!game.global_enabled && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-gray-500/20 text-gray-400 flex-shrink-0">Off globally</span>}
        </div>
        <p className="text-[11px] text-gray-500 truncate capitalize">{formatProvider(game.provider_code) || '—'}</p>
      </div>
      <button
        onClick={onToggle}
        disabled={saving}
        className={`flex items-center gap-1.5 px-3 h-9 rounded-full text-xs font-semibold transition ${
          game.tenant_enabled
            ? 'bg-green-500/15 text-green-400 border border-green-500/30 hover:bg-green-500/25'
            : 'bg-white/5 text-gray-400 border border-white/10 hover:bg-white/10'
        }`}
        data-testid={`toggle-game-${game.id}`}
        aria-pressed={game.tenant_enabled}
      >
        {saving ? (
          <RefreshCw className="w-3.5 h-3.5 animate-spin" />
        ) : game.tenant_enabled ? (
          <ToggleRight className="w-4 h-4" />
        ) : (
          <ToggleLeft className="w-4 h-4" />
        )}
        {game.tenant_enabled ? 'Enabled' : 'Disabled'}
      </button>
    </div>
  );
}

function ProviderCard({ provider, saving, onToggle }) {
  return (
    <div
      className={`rounded-xl p-4 border transition ${
        provider.tenant_enabled
          ? 'border-white/10 bg-[#131822]'
          : 'border-white/5 bg-[#131822]/60 opacity-75'
      }`}
      data-testid={`provider-card-${provider.provider_code}`}
    >
      <div className="flex items-center justify-between gap-3 mb-2">
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-9 h-9 rounded-lg bg-[#8b5cf6]/15 flex items-center justify-center flex-shrink-0">
            <Server className="w-5 h-5 text-[#8b5cf6]" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-white truncate">{formatProvider(provider.provider_code)}</p>
            <p className="text-[11px] text-gray-500">
              {provider.game_count} games · {provider.enabled_game_count} active globally
            </p>
          </div>
        </div>
        <button
          onClick={onToggle}
          disabled={saving}
          className={`flex items-center gap-1.5 px-3 h-9 rounded-full text-xs font-semibold transition ${
            provider.tenant_enabled
              ? 'bg-green-500/15 text-green-400 border border-green-500/30 hover:bg-green-500/25'
              : 'bg-white/5 text-gray-400 border border-white/10 hover:bg-white/10'
          }`}
          data-testid={`toggle-provider-${provider.provider_code}`}
          aria-pressed={provider.tenant_enabled}
        >
          {saving ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> :
            provider.tenant_enabled ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}
          {provider.tenant_enabled ? 'Enabled' : 'Disabled'}
        </button>
      </div>
      <div className="flex items-center gap-2 text-[11px]">
        {provider.tenant_enabled ? (
          <span className="inline-flex items-center gap-1 text-green-400">
            <CheckCircle2 className="w-3 h-3" /> Visible to your players
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 text-gray-500">
            <XCircle className="w-3 h-3" /> Hidden from your players
          </span>
        )}
      </div>
    </div>
  );
}

function EmptyState({ message }) {
  return (
    <div className="flex flex-col items-center justify-center py-12 border border-dashed border-white/10 rounded-xl text-gray-500 text-sm">
      <Gamepad2 className="w-8 h-8 mb-2 opacity-50" />
      {message}
    </div>
  );
}

function formatProvider(code) {
  if (!code) return '';
  return code.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function resolveUrl(u) {
  if (!u) return '';
  return u.startsWith('http') ? u : `${API_BASE}${u}`;
}
